import { useMutation, useQuery } from "@apollo/client";
import { toast } from "sonner";
import { useSalonStore } from "@/lib/store/salon";
import {
  GET_WORKER_ALLOWED_SERVICES,
  REMOVE_WORKER_SERVICE_RESTRICTIONS,
  UPDATE_WORKER_SERVICE_RESTRICTION,
} from "../graphql/worker-service-restrictions";

interface UpdateRestrictionInput {
  workerSalonId: string;
  serviceIds: string[];
  categoryIds: string[];
}

/**
 * Hook for managing worker service restrictions
 */
export function useWorkerServiceRestrictions(workerSalonId?: string) {
  const currentSalon = useSalonStore((state) => state.currentSalon);

  // Query to get allowed services
  const {
    data: allowedServicesData,
    loading: loadingAllowedServices,
    error: allowedServicesError,
    refetch: refetchAllowedServices,
  } = useQuery(GET_WORKER_ALLOWED_SERVICES, {
    variables: { workerSalonId: workerSalonId || "" },
    skip: !workerSalonId || !currentSalon?.id,
    fetchPolicy: "cache-and-network",
  });

  // Mutation to update restrictions
  const [updateRestrictionMutation, { loading: updating }] = useMutation(
    UPDATE_WORKER_SERVICE_RESTRICTION,
    {
      refetchQueries: ["TeamMembers", "GetWorkerAllowedServices"],
      awaitRefetchQueries: true,
      onCompleted: () => {
        toast.success("Service restrictions updated successfully");
      },
      onError: (error) => {
        console.error("Error updating service restrictions:", error);
        toast.error(error.message || "Failed to update service restrictions");
      },
    }
  );

  // Mutation to remove all restrictions (reset to ALLOW_ALL)
  const [removeRestrictionsMutation, { loading: removing }] = useMutation(
    REMOVE_WORKER_SERVICE_RESTRICTIONS,
    {
      refetchQueries: ["TeamMembers", "GetWorkerAllowedServices"],
      awaitRefetchQueries: true,
      onCompleted: () => {
        toast.success("Service restrictions removed successfully");
      },
      onError: (error) => {
        console.error("Error removing service restrictions:", error);
        toast.error(error.message || "Failed to remove service restrictions");
      },
    }
  );

  /**
   * Update worker service restrictions to ALLOW_SPECIFIC mode
   */
  const updateRestrictions = async (input: UpdateRestrictionInput) => {
    if (!input.serviceIds.length && !input.categoryIds.length) {
      toast.error("Please select at least one service or category");
      return;
    }

    await updateRestrictionMutation({
      variables: {
        input: {
          workerSalonId: input.workerSalonId,
          serviceRestrictionMode: "ALLOW_SPECIFIC" as const,
          serviceIds: input.serviceIds,
          categoryIds: input.categoryIds,
        },
      },
    });
  };

  /**
   * Remove all restrictions (reset to ALLOW_ALL mode)
   */
  const removeRestrictions = async (workerSalonId: string) => {
    await removeRestrictionsMutation({
      variables: { workerSalonId },
    });
  };

  return {
    // Data
    allowedServices: allowedServicesData?.getWorkerAllowedServices || [],

    // Loading states
    loading: loadingAllowedServices,
    updating,
    removing,

    // Error
    error: allowedServicesError,

    // Actions
    updateRestrictions,
    removeRestrictions,
    refetchAllowedServices,
  };
}
